import { useEffect, useState } from "react";
import { useTeam } from "../../../context/TeamContext";
import { TeamMemberWithDetails } from "../../../types/team_members.types";
import { TeamMemberForm } from "./team-member-form";
import { useToast } from "../../../components/toast";
import { getObjectDiff } from "../../../utils/object-diff.utils";
import { TeamMembersAPI } from "../../../APIs/team_members.api";
import { PersonalInfoApiError } from "../../../types/personal_info.types";
import { Loading } from "../../../components/loading";

export interface ITeamMemberEditProps {
  userId: number;
  onCancel: () => void;
}

export function TeamMemberEdit({ userId, onCancel }: ITeamMemberEditProps) {
  const { getTeam } = useTeam();
  const [modifiedFormData, setModifiedFormData] =
    useState<TeamMemberWithDetails | null>(null);
  const [originalFormData, setOriginalFormData] =
    useState<TeamMemberWithDetails | null>(null);
  const { addToast } = useToast();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [apiErrors, setApiErrors] = useState<PersonalInfoApiError>({
    dni: null,
    email: null,
    licence_number: null,
  });

  useEffect(() => {
    const fetchTeamMember = async () => {
      setIsFetching(true);
      if (userId) {
        const result = await TeamMembersAPI.getOneTeamMember(userId);
        if (result.statusCode === 200) {
          setModifiedFormData(result.data);
          setOriginalFormData(result.data);
        } else {
          addToast({
            message: "No se encontró el miembro",
            type: "error",
            location: "bottom-center",
            duration: 10000,
          });
          onCancel();
        }
        setIsFetching(false);
      }
    };

    fetchTeamMember();
  }, [userId, onCancel, addToast]);

  const handleChange = (e: React.ChangeEvent<HTMLElement>, index?: number) => {
    setApiErrors({
      dni: null,
      email: null,
      licence_number: null,
    });
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value, type } = target;

    setModifiedFormData((prev) => {
      if (!prev) return prev;

      if (type === "checkbox") {
        return {
          ...prev,
          [name]: (target as HTMLInputElement).checked,
        };
      }

      if (index !== undefined && prev.addresses) {
        return {
          ...prev,
          addresses: prev.addresses.map((address, i) =>
            i === index ? { ...address, [name]: value } : address
          ),
        };
      }

      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        const parentKey = parent as keyof TeamMemberWithDetails;
        const parentObject = prev[parentKey];

        if (parentObject && typeof parentObject === "object") {
          return {
            ...prev,
            [parent]: {
              ...parentObject,
              [child]: value,
            },
          };
        }

        return {
          ...prev,
          [parent]: {
            [child]: value,
          },
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const changes = getObjectDiff(originalFormData, modifiedFormData);

      const { id, user_id, personal_info, ...changesWithoutIds } = changes;

      if (personal_info && "user_id" in personal_info) {
        delete personal_info.user_id;
      }

      if (personal_info) {
        changesWithoutIds.personal_info = personal_info;
      }

      const response = await TeamMembersAPI.updateTeamMember(
        user_id,
        changesWithoutIds
      );
      if (response.statusCode === 200) {
        getTeam();
        addToast({
          message: "¡Miembro actualizado!",
          type: "success",
          location: "bottom-center",
        });
        onCancel();
      } else {
        const errorCode = response.additionalData?.errorCode;
        if (errorCode === "DNI_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            dni: "Este DNI ya está en uso.",
          }));
        } else if (errorCode === "EMAIL_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            email: "Este correo electrónico ya está en uso.",
          }));
        } else if (errorCode === "LICENSE_NUMBER_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            licence_number: "Este número de licencia ya está en uso.",
          }));
        }
        addToast({
          message: response.message,
          type: "error",
          location: "bottom-center",
          duration: 10000,
        });
      }
    } catch (error) {
      console.error(error);
      addToast({
        message: "Error al actualizar el miembro, intente nuevamente",
        type: "error",
        location: "bottom-center",
        duration: 10000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!modifiedFormData) return null;

  if (isFetching) {
    return <Loading />;
  }

  return (
    <TeamMemberForm
      buttonText="Guardar"
      teamMember={modifiedFormData}
      onCancel={onCancel}
      onSubmit={handleSubmit}
      handleChange={handleChange}
      isSubmitting={isSubmitting}
      personalInfoApiError={apiErrors}
    />
  );
}
